<template>
  <div v-if="isLoading" class="w-full flex flex-col gap-4 overflow-y-auto h-full">
    <Skeleton v-for="i in [1,2,3]" height="9rem" />
  </div>
  <div v-else class="w-full flex flex-col gap-4 overflow-y-auto h-full relative">
    <div v-if="appointments.length" class="w-full flex flex-col gap-4 overflow-y-auto h-full flex-none pb-24">
      <div v-for="appointment in appointments"
           :ref="el => setAppointmentRef(el, appointment.appointmentId)"
           class="border p-3 rounded flex flex-col gap-3"
           :class="{
           '!bg-yellow-50 !border-yellow-300 !shadow-lg': appointment.appointmentId === targetAppointmentId,
           'bg-gray-100' : appointment.status === AppointmentStatus.CANCELLED,
           'bg-orange-50' : appointment.status === AppointmentStatus.NO_SHOW,
         }"
      >
      <span v-if="calculateAvailableOptions(appointment).length > 0"
            class="w-full flex justify-end px-1 md:py-1 -mb-2 cursor-pointer">
        <i class="pi pi-ellipsis-h" @click="openOptions(appointment)" />
      </span>
        <service-item v-for="service of appointment.services" :service="service" mode="payment" size="small" class="!cursor-default"/>
        <div class="grid grid-cols-2 gap-2 w-full">
          <div class="flex flex-col w-full">
            <span class="text-xs text-gray-500">תאריך ושעה</span>
            <span class="text-sm">{{ DateTime.fromISO(appointment.date).toLocaleString(DateTime.DATE_MED)
              }} {{ appointment.startHour }}</span>
          </div>
          <div class="flex flex-col w-full">
            <span class="text-xs text-gray-500">עובד/ת</span>
            <span class="text-sm">{{ providers[appointment.provider] }}</span>
          </div>
        </div>

        <!--   Note   -->
        <div
          v-if="appointment.adminNote || appointment.images?.length"
          class="flex flex-col w-full justify-start items-start mt-2">
          <span v-if="appointment.adminNote" class="text-xs text-gray-500">הערות</span>
          <span v-if="appointment.adminNote" class="text-sm mb-2">{{ appointment.adminNote }}</span>

          <div
            v-if="appointment.images?.length > 0"
            class="flex gap-1 mt-2"
          >
            <Image
              v-for="img of appointment.images"
              :key="img"
              alt="תמונה שצורפה לביקור"
              :src="img"
              class="max-w-[4rem] ml-2 !rounded overflow-hidden"
              preview
            />
          </div>
        </div>

        <!--       Customer Review         -->
        <div v-if="appointment.review"
             class="grid grid-cols-2 gap-2 w-full">
          <div class="flex flex-col w-full">
            <span class="text-xs text-gray-500">דירוג לקוח</span>
            <span class="text-sm">{{ appointment?.review?.rank ? rankMapping[appointment?.review?.rank] : 'ללא דירוג'
              }}</span>
          </div>
          <div class="flex flex-col w-full">
            <span class="text-xs text-gray-500">משוב מהלקוח</span>
            <span class="text-sm">{{ appointment?.review?.review ?? 'ללא משוב' }}</span>
          </div>
        </div>

        <div class="flex flex-col w-full gap-2">
          <span class="text-xs text-gray-500">סטטוס</span>
          <span v-if="isAppointmentComplete(appointment)"
                class="text-left text-sm text-green-500 flex gap-1 items-center px-3 py-1 rounded-lg bg-green-50 w-max flex-none h-min">
              <i class="pi pi-check" style="font-size: 0.75rem" />
              <span>תור הושלם</span>
        </span>
          <span v-else-if="appointment.status === AppointmentStatus.NO_SHOW"
                class="text-left text-sm text-orange-500 flex gap-1 items-center px-3 py-1 rounded-lg bg-orange-50 w-max flex-none h-min">
              <i class="pi pi-user-minus" style="font-size: 0.75rem" />
              <span>לא הגיע/ה</span>
        </span>
          <span v-else-if="appointment.status === AppointmentStatus.CANCELLED && !appointment.cancellation"
                class="text-left text-sm text-red-500 flex gap-1 items-center px-3 py-1 rounded-lg bg-red-50 w-max flex-none h-min">
              <i class="pi pi-times" style="font-size: 0.75rem" />
              <span>תור בוטל</span>
        </span>
          <!-- When we recorded who/when/why, the detail card replaces the plain chip. -->
          <CancellationInfo
            v-else-if="appointment.status === AppointmentStatus.CANCELLED"
            :cancellation="appointment.cancellation!" />
          <span v-else
                class="text-left text-sm text-blue-500 flex gap-1 items-center px-3 py-1 rounded-lg bg-blue-50 w-max flex-none h-min">
              <span>תור עתידי</span>
        </span>
        </div>

        <!-- Payment for this visit: amount + status; opens the payment details when there's
             something to see there (paid / refunded) -->
        <component
          :is="canOpenPayment(paymentOf(appointment)) ? 'button' : 'div'"
          v-if="appointment.paymentRequestId && paymentOf(appointment)"
          :type="canOpenPayment(paymentOf(appointment)) ? 'button' : undefined"
          class="w-full flex items-center gap-2.5 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-start"
          :class="canOpenPayment(paymentOf(appointment)) && 'active:bg-gray-50 cursor-pointer'"
          @click="canOpenPayment(paymentOf(appointment)) && openPayment(paymentOf(appointment)!)"
        >
          <span class="w-8 h-8 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center flex-none">
            <i class="pi pi-receipt !text-sm" />
          </span>
          <span class="flex-1 min-w-0 flex flex-col">
            <span class="text-[13px] font-medium text-gray-900">פרטי תשלום</span>
            <span class="text-[11.5px] text-gray-500">{{ paymentSubtitle(paymentOf(appointment)!) }}</span>
          </span>
          <span class="text-[13px] font-semibold text-gray-900 flex-none" style="direction: ltr">₪{{ paymentAmount(paymentOf(appointment)!) }}</span>
          <span class="text-[10.5px] px-1.5 py-0.5 rounded-full flex-none" :class="paymentChip(paymentOf(appointment)!).class">
            {{ paymentChip(paymentOf(appointment)!).label }}
          </span>
          <i v-if="canOpenPayment(paymentOf(appointment))" class="pi pi-chevron-left text-gray-300 !text-xs flex-none" />
        </component>
      </div>
    </div>
    <div v-else class="w-full h-full flex flex-col justify-start items-center my-auto">
      <span class="text-sm">לא נקבעו תורים</span>
    </div>

    <div v-if="isMobile" class="bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)] absolute bottom-0 w-full p-4 right-0 left-0">
      <Button label="זימון תור" class="w-full" @click="addManualClientAppointment"/>
    </div>

  </div>

</template>

<script setup lang="ts">

import {
  AppointmentStatus,
  CustomerTimelineType,
  ICustomer,
  ICustomerTimeline,
  IPaymentsResponse,
  IService,
  PaymentMethod,
  PaymentRequestStatus,
  rankMapping
} from '@torim-app/types';
import { ComponentPublicInstance, computed, nextTick, onMounted, PropType, ref, watch } from 'vue';
import { useAdminStore } from '@app/stores';
import { ServiceItem, useDrawer } from '@torim-app/shared-components';
import { useRoute } from 'vue-router';
import { CustomersApi } from '@app/api/admin';
import { useToast } from 'vue-toastification';
import { DateTime } from 'luxon';
import CancellationInfo from '@app/components/admin/components/CancellationInfo.vue';
import CustomerAppointmentOptions from '@app/components/admin/pages/customer/CustomerAppointmentOptions.vue';
import PaymentDetailsDrawer from '@app/components/admin/pages/customer/PaymentDetailsDrawer.vue';
import NewCustomerEvent from '@app/components/admin/modals/NewCustomerEvent.vue';
import { useResponsive } from '@app/composables/responsive';
import { trackEvent } from '@app/composables/posthog';
const { isMobile } = useResponsive();

const route = useRoute();
const adminStore = useAdminStore();
const { closeDrawer, openDrawer } = useDrawer();
const toast = useToast();

const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true,
    default: () => {
    }
  }
});

const appointments = ref<Array<ICustomerTimeline & { services: IService[] }>>([]);
const appointmentRefs = ref<Record<string, HTMLElement>>({});
const isLoading = ref<boolean>(true);

const targetAppointmentId = computed(() => {
  return route.query.apid as string || '';
});
const providers = computed<Record<string /** Provider Id **/, string /** full name **/>>(() => {
  const res: Record<string, string> = {};
  for (const provider of adminStore.getProviders) {
    res[provider.userId] = provider.fullName;
  }
  return res;
});

/**
 * Whether the appointment's day is over.
 *
 * `appointment.date` is the day at midnight, so it has to be compared against the END of
 * that day: measuring the appointment's midnight against *today's* end of day made every
 * appointment happening today look like it had already been and gone.
 */
const appointmentDayHasPassed = (appointment: ICustomerTimeline): boolean =>
  DateTime.fromISO(appointment.date).endOf('day').diffNow('days').days < 0;

// Explicitly finished, or booked on a day that has since passed.
const isAppointmentComplete = (appointment: ICustomerTimeline): boolean =>
  appointment.status === AppointmentStatus.FINISHED
  || (([AppointmentStatus.VERIFIED, AppointmentStatus.APPROVED] as AppointmentStatus[]).includes(appointment.status)
    && appointmentDayHasPassed(appointment));

const calculateAvailableOptions = (appointment: ICustomerTimeline) => {
  const actions: Array<'cancel' | 'edit' | 'note'> = [];
  if (([AppointmentStatus.VERIFIED, AppointmentStatus.APPROVED] as AppointmentStatus[]).includes(appointment.status) && !appointmentDayHasPassed(appointment)) {
    actions.push('cancel');
    actions.push('edit');
  }
  if (appointment.status !== AppointmentStatus.CANCELLED)
    actions.push('note');

  return actions;
};

const addManualClientAppointment = () => {
  trackEvent('customer_timeline_schedule_clicked');
  openDrawer(NewCustomerEvent, {
    onClose: closeDrawer
  }, {
    title: 'זימון תור',
    closable: true,
    noPadding: true
  });
};

const openOptions = (appointment: ICustomerTimeline) => {
  trackEvent('customer_appointment_options_opened', { appointment_status: appointment.status });
  openDrawer(CustomerAppointmentOptions, {
    actions: calculateAvailableOptions(appointment),
    appointment,
    onCompleted: async () => {
      closeDrawer();
      await fetchHistory();
    },
    onClose: () => {
      closeDrawer();
    }
  }, {
    title: 'פעולות',
    noPadding: true
  });
};


// Function to set appointment ref
const setAppointmentRef = (el: Element | ComponentPublicInstance | null, appointmentId: string) => {
  if (el && el instanceof HTMLElement) {
    appointmentRefs.value[appointmentId] = el;
  }
};

// Function to scroll to the target appointment
const scrollToAppointment = () => {
  if (targetAppointmentId.value && appointmentRefs.value[targetAppointmentId.value]) {
    nextTick(() => {
      const targetElement = appointmentRefs.value[targetAppointmentId.value];
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    });
  }
};

// The customer's payments by id, so each visit can show (and open) its own payment.
const paymentsById = ref<Record<string, IPaymentsResponse>>({});

const paymentOf = (appointment: ICustomerTimeline): IPaymentsResponse | undefined =>
  appointment.paymentRequestId ? paymentsById.value[String(appointment.paymentRequestId)] : undefined;

// Same rule as the payments tab: the details drawer is for money that actually moved.
const canOpenPayment = (payment?: IPaymentsResponse) => !!payment && ([
  PaymentRequestStatus.Success,
  PaymentRequestStatus.PartiallyRefunded,
  PaymentRequestStatus.Refunded
] as PaymentRequestStatus[]).includes(payment.status);

const paymentAmount = (payment: IPaymentsResponse) =>
  (payment.paidPrice ?? payment.price ?? 0).toLocaleString('he-IL', { maximumFractionDigits: 2 });

const paymentMethodLabels: Partial<Record<PaymentMethod, string>> = {
  [PaymentMethod.Credit]: 'כרטיס אשראי',
  [PaymentMethod.Cash]: 'מזומן',
  [PaymentMethod.BankTransfer]: 'העברה בנקאית',
  [PaymentMethod.Bit]: 'ביט',
  [PaymentMethod.PayBox]: 'פייבוקס',
};

const paymentSubtitle = (payment: IPaymentsResponse) => {
  // Fully covered by the customer's credit: recorded as a ₪0 cash payment, but it wasn't cash
  const coveredByBalance = (payment.paidPrice ?? payment.price ?? 0) <= 0 && (payment.paymentSplit?.balanceDiscount?.amount ?? 0) > 0;
  const method = coveredByBalance ? 'יתרת לקוח' : (paymentMethodLabels[payment.paymentMethod] ?? '');
  const date = payment.chargedDate ? DateTime.fromISO(String(payment.chargedDate)).toFormat('dd/MM/yy') : '';
  return [method, date].filter(Boolean).join(' · ') || 'בקשת תשלום';
};

const paymentChip = (payment: IPaymentsResponse): { label: string; class: string } => {
  switch (payment.status) {
    case PaymentRequestStatus.Success: return { label: 'שולם', class: 'bg-green-50 text-green-600' };
    case PaymentRequestStatus.Refunded: return { label: 'הוחזר', class: 'bg-orange-50 text-orange-500' };
    case PaymentRequestStatus.PartiallyRefunded: return { label: 'הוחזר חלקית', class: 'bg-orange-50 text-orange-500' };
    case PaymentRequestStatus.Scheduled: return { label: 'כרטיס שמור', class: 'bg-blue-50 text-blue-500' };
    case PaymentRequestStatus.InProgress: return { label: 'בתהליך', class: 'bg-gray-100 text-gray-500' };
    default: return { label: 'נשלחה בקשה', class: 'bg-gray-100 text-gray-500' };
  }
};

const openPayment = (payment: IPaymentsResponse) => {
  trackEvent('customer_timeline_payment_opened', { payment_status: payment.status });
  openDrawer(PaymentDetailsDrawer, {
    selectedPayment: payment,
    onRefunded: (updated: IPaymentsResponse) => {
      paymentsById.value = { ...paymentsById.value, [updated._id!]: { ...payment, ...updated } };
    }
  }, { title: 'פרטי תשלום' });
};

const fetchHistory = async () => {
  isLoading.value = true;
  try {
    const [apHistory, payments] = await Promise.all([
      CustomersApi.fetchCustomerHistory(props.customer.id),
      // Optional extra: a business without payments, or a failure, just hides the rows
      CustomersApi.getPaymentsInformation(props.customer.id).catch(() => null)
    ]);
    paymentsById.value = Object.fromEntries((payments?.paymentsHistory ?? []).map(p => [String(p._id), p]));
    appointments.value = apHistory.map(ap => {
      const services = adminStore.getServices.filter(s => ap.servicesIds?.includes(s.id));
      return { ...ap, services };
    });
  } catch (e) {
    console.log(e);
    toast.error('לא הצלחנו לטעון את כל התורים.');
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in appointments or targetAppointmentId
watch(
  [() => appointments.value, () => targetAppointmentId.value],
  () => {
    if (targetAppointmentId.value && appointments.value.length > 0) {
      setTimeout(scrollToAppointment, 100);
    }
  },
  { immediate: true }
);

onMounted(() => {
  fetchHistory();
});

</script>

